import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectContracts } from '../../../store/dataSelectors';
import {
    clearServiceDetails,
    loadContractDetails,
    loadServiceDetailsByContract
} from '../../../store/directusService';
import { loadRecentParticipants } from '../../../store/participantService';
import { selectUser, selectUserTypes } from '../../../store/userSelectors';
import {
    clearBusinessLogicError,
    clearTeamsForService,
    loadTeamsForService,
    loadUserTypes,
    mfaRequest,
    updateUser
} from '../../../store/userService';
import { compareUnorderedArrays } from '../../../utils/arrayUtils';
import { debounce } from '../../../utils/debounce';
import { MAX_EMAIL } from '../../../utils/formValidation/commonConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import SingleSelect from '../../formElements/SingleSelect';
import Switch from '../../formElements/Switch';
import TextInputField from '../../formElements/TextInputField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ConfirmPrompt from '../../ui/notices/confirmPrompt/ConfirmPrompt';

import {
    checkEmailAddress,
    EMAIL_EXISTS,
    MAX_JOB_TITLE_CHAR,
    MAX_NAME_CHAR,
    MAX_PAYROLL_CHAR,
    MAX_PHONE_CHAR,
    onValidate
} from './validateManageUsers';

import '../userStyles/editUserDetails.css';
import form from '../../../commonStyles/formStyles.module.css';
import pillClasses from '../../../commonStyles/staticPillList.module.css';
import local from './manageUsers.module.css';

const acceptedRoles = [SUPERUSER, QUALITY];
const _MFA_CONFIRM_MESSAGE =
    'This will remove any existing 2FA link against this user’s account and their device when they next log in. The user will have to re-set up the MFA link to their device within their Microsoft authenticator app, by scanning the QR code presented on screen and following the instructions presented. Users will be unable to access Polaris until this process has been completed.';
const _MFA_REQUEST_SENT_MESSAGE = 'Reset Multi Factor Authentication reset completed';

const EditUsersDetails = ({ row, onClose, onCancel }) => {
    const dispatch = useDispatch();
    const onSelectUser = useMemo(selectUser, []);

    // LOCAL STATE
    const INITIAL_STATE = {
        locked: false,
        active: true,
        id: '',
        payroll: '',
        firstName: '',
        lastName: '',
        loginEmailAddress: '',
        communicationEmailAddress: '',
        jobTitle: '',
        phoneNumber: '',
        primaryTeamId: '',
        otherTeamIds: [],
        primaryServiceId: '',
        otherServiceIds: [],
        contractIds: [],
        userTypeIds: [],
        code: null
    };

    const [newEntry, setNewEntry] = useState(INITIAL_STATE);
    const [initialUser, setInitialUser] = useState({});

    const [usersContracts, setUsersContracts] = useState([]);

    const [usersOtherServices, setUsersOtherServices] = useState([]);
    const [otherServices, setOtherServices] = useState([]);
    const [servicesForQuery, setServicesForQuery] = useState([]);

    const [usersOtherTeams, setUsersOtherTeams] = useState([]);
    const [otherTeams, setOtherTeams] = useState([]);

    const [selectedUserTypeIds, setSelectedUserTypeIds] = useState([]);
    const [preventUserTypeChange, setPreventUserTypeChange] = useState(true);

    const [keys, setKeys] = useState({
        userTypeIds: '0',
        newServices: '1',
        newTeams: '2',
        newContracts: '3',
        primaryService: '4',
        primaryTeam: '5'
    });

    const [errors, setErrors] = useState({});
    const [showMfaConfirmPrompt, setShowMfaConfirmPrompt] = useState(false);
    const [isMfaButtonDisabled, setIsMfaButtonDisabled] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const roles = loggedInUser.roles;
    const user = useSelector((state) => onSelectUser(state, row.id));
    const userTypes = useSelector(selectUserTypes);
    const contracts = useSelector(selectContracts);
    const services = useSelector((state) => state.entities.directusService.serviceDetails);
    const loadingServiceDetails = useSelector(
        (state) => state.entities.directusService.loadingServiceDetails
    );
    const teamsForServices = useSelector((state) => state.entities.userService.teamsForService);
    const loadingTeamsForService = useSelector(
        (state) => state.entities.userService.loadingTeamsForService
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const businessLogicError = useSelector(
        (state) => state.entities.userService.businessLogicError
    );

    // USE EFFECTS
    useEffect(() => {
        return () => {
            debouncedCallToLoadServices.current.cancel?.();
            debouncedCallToUpdateServices.current.cancel?.();
            debouncedCallToUpdateTeams.current.cancel?.();
        };
    }, []);

    useEffect(() => {
        if (!userTypes.length) dispatch(loadUserTypes());
        dispatch(loadContractDetails());
        setKeys((prev) => ({
            ...prev,
            newServices: Math.random(),
            newTeams: Math.random()
        }));
    }, []);

    useEffect(() => {
        if (!user?.id) return;
        if (user.contracts?.length > 0) {
            const contractIds = contracts
                .filter((el) => user.contracts.includes(el.id))
                .map((el) => el.id);
            if (contractIds.length) dispatch(loadServiceDetailsByContract(contractIds));
        }
        const userTypeIds = user.userTypes?.map((el) => el.id);
        setNewEntry((prev) => ({
            ...prev,
            id: user.id,
            active: user.active,
            code: user.code,
            payroll: user.payroll,
            firstName: user.firstName,
            lastName: user.lastName,
            loginEmailAddress: user.loginEmailAddress,
            communicationEmailAddress: user.communicationEmailAddress,
            jobTitle: user.jobTitle,
            phoneNumber: user.phoneNumber,
            primaryTeamId: user.primaryTeam?.id,
            otherTeamIds: user.otherTeams.map((el) => el.id) || [],
            primaryServiceId: user.primaryService,
            otherServiceIds: user.otherServices,
            contractIds: user.contracts,
            userTypeIds
        }));
        setSelectedUserTypeIds(userTypeIds);
        if (!Object.keys(initialUser).length) {
            setInitialUser(user);
        }
    }, [user]);

    useEffect(() => {
        if (!newEntry.id) return;
        if (
            loggedInUser.id === newEntry.id &&
            (!compareUnorderedArrays(newEntry.contractIds, initialUser.contracts) ||
                !compareUnorderedArrays(newEntry.otherServiceIds, initialUser.otherServices) ||
                !compareUnorderedArrays(
                    newEntry.otherTeamIds,
                    initialUser.otherTeams.map((el) => el.id)
                ) ||
                newEntry.primaryServiceId !== initialUser.primaryService ||
                newEntry.primaryTeamId !== (initialUser.primaryTeam?.id ?? ''))
        ) {
            dispatch(loadRecentParticipants());
        }
    }, [user]);

    useEffect(() => {
        const superUserTypeId = userTypes.find((el) => el.role === SUPERUSER)?.id;
        setPreventUserTypeChange(
            selectedUserTypeIds?.includes(superUserTypeId) && newEntry.id === loggedInUser.id
        );
    }, [selectedUserTypeIds, userTypes, roles]);

    useEffect(() => {
        if (contracts.length === 0) return;

        const selectedContracts = contracts.filter((el) => newEntry.contractIds.includes(el.id));
        const selectedContractIds = selectedContracts.map((el) => el.id);

        if (!compareUnorderedArrays(newEntry.contractIds, selectedContractIds))
            setNewEntry((prev) => ({ ...prev, contractIds: selectedContractIds }));
        else {
            setUsersContracts(selectedContracts);
            if (!selectedContractIds.length) {
                dispatch(clearServiceDetails());
            } else {
                dispatch(loadServiceDetailsByContract(selectedContractIds));
            }
        }
    }, [contracts, newEntry.contractIds]);

    useEffect(() => {
        if (!newEntry.id.length || loadingServiceDetails) return;
        if (!services?.length) {
            setOtherServices([]);
            setNewEntry((prev) => ({ ...prev, primaryServiceId: '', otherServiceIds: [] }));
            dispatch(clearTeamsForService());
            return;
        }
        const serviceIds = services.map((el) => el.id);
        const updatedServices = newEntry.otherServiceIds.filter((el) => serviceIds.includes(el));
        if (!compareUnorderedArrays(updatedServices, newEntry.otherServiceIds))
            setNewEntry((prev) => ({ ...prev, otherServiceIds: updatedServices }));
    }, [services, loadingServiceDetails]);

    useEffect(() => {
        if (!newEntry.id.length || loadingServiceDetails) return;

        setUsersOtherServices(services.filter((el) => newEntry.otherServiceIds.includes(el.id)));

        const availableServices = services.filter((el) => el.id !== newEntry.primaryServiceId);

        const primaryServiceId = services.map((el) => el.id).includes(newEntry.primaryServiceId)
            ? newEntry.primaryServiceId
            : '';
        setOtherServices(availableServices);
        if (newEntry.primaryServiceId !== primaryServiceId)
            setNewEntry((prev) => ({ ...prev, primaryServiceId: primaryServiceId }));
        if (newEntry.otherServiceIds.includes(primaryServiceId))
            setNewEntry((prev) => ({
                ...prev,
                otherServiceIds: newEntry.otherServiceIds.filter((el) => el !== primaryServiceId)
            }));

        const queryArray = [
            ...(primaryServiceId.length > 0 ? [primaryServiceId] : []),
            ...newEntry.otherServiceIds
        ];
        if (!queryArray.length) {
            setServicesForQuery([]);
            dispatch(clearTeamsForService());
        } else if (!compareUnorderedArrays(queryArray, servicesForQuery)) {
            setServicesForQuery(queryArray);
            dispatch(
                loadTeamsForService({
                    serviceIds: queryArray
                })
            );
        }
    }, [newEntry.otherServiceIds, newEntry.primaryServiceId, loadingServiceDetails]);

    useEffect(() => {
        if (!newEntry.id.length || loadingTeamsForService || loadingServiceDetails) return;
        if (!teamsForServices.length) {
            setOtherTeams([]);
            setNewEntry((prev) => ({ ...prev, primaryTeamId: '', otherTeamIds: [] }));
            return;
        }
        const teamIds = teamsForServices.map((el) => el.id);
        const updatedTeams = newEntry.otherTeamIds.filter((el) => teamIds.includes(el));
        if (!compareUnorderedArrays(updatedTeams, newEntry.otherTeamIds))
            setNewEntry((prev) => ({ ...prev, otherTeamIds: updatedTeams }));
    }, [teamsForServices, loadingTeamsForService]);

    useEffect(() => {
        if (!newEntry.id.length || loadingTeamsForService || loadingServiceDetails) return;
        setUsersOtherTeams(teamsForServices.filter((el) => newEntry.otherTeamIds.includes(el.id)));

        const availableTeams = teamsForServices.filter((el) => el.id !== newEntry.primaryTeamId);
        const primaryTeamId = teamsForServices.map((el) => el.id).includes(newEntry.primaryTeamId)
            ? newEntry.primaryTeamId
            : '';
        if (newEntry.primaryTeamId !== primaryTeamId)
            setNewEntry((prev) => ({ ...prev, primaryTeamId: primaryTeamId }));
        if (newEntry.otherTeamIds.includes(primaryTeamId))
            setNewEntry((prev) => ({
                ...prev,
                otherTeamIds: newEntry.otherTeamIds.filter((el) => el !== primaryTeamId)
            }));
        setOtherTeams(availableTeams);
    }, [newEntry.otherTeamIds, newEntry.primaryTeamId, loadingTeamsForService]);

    useEffect(() => {
        if (businessLogicError && Object.keys(businessLogicError).length > 0) {
            const oathMfaError = Object.values(businessLogicError)?.find(
                (el) => el.message.includes('OATH MFA') || el.message.includes('MFA registration')
            );
            if (oathMfaError) setErrors((errors) => ({ ...errors, oathMfa: oathMfaError }));
            dispatch(clearBusinessLogicError());
        }
    }, [businessLogicError]);

    useEffect(() => {
        if (successMessage === `Updated user ${newEntry.loginEmailAddress}`) {
            clearUser();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearUser = () => {
        setKeys(clearKeys(keys));
        setNewEntry(INITIAL_STATE);
        onClose();
    };

    const debouncedCallToLoadServices = useRef(
        debounce((ids) => {
            setNewEntry((prev) => ({
                ...prev,
                contractIds: ids
            }));

            clearError('primaryServiceId');
            clearError('primaryTeamId');
        }, 500)
    );

    const debouncedCallToUpdateServices = useRef(
        debounce((ids) => {
            setNewEntry((prev) => ({ ...prev, otherServiceIds: ids }));
        }, 500)
    );

    const debouncedCallToUpdateTeams = useRef(
        debounce((ids) => {
            setNewEntry((prev) => ({ ...prev, otherTeamIds: ids }));
        }, 500)
    );



    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS

    const onLockedChange = (e) => {
        setNewEntry((prev) => ({
            ...prev,
            locked: e.target.checked
        }));
    };

    const onActiveChange = (e) => {
        setNewEntry((prev) => ({
            ...prev,
            active: e.target.checked
        }));
    };

    const onPayrollChange = (e) => {
        setNewEntry((prev) => ({
            ...prev,
            payroll: e.target.value.trim().slice(0, MAX_PAYROLL_CHAR)
        }));
    };

    const onFirstNameChange = (e) => {
        clearError('firstName');
        setNewEntry((prev) => ({
            ...prev,
            firstName: e.target.value.trim().slice(0, MAX_NAME_CHAR)
        }));
    };

    const onLastNameChange = (e) => {
        clearError('lastName');
        setNewEntry((prev) => ({
            ...prev,
            lastName: e.target.value.trim().slice(0, MAX_NAME_CHAR)
        }));
    };

    const onLoginEmailChange = (e) => {
        clearError('loginEmailAddress');
        setNewEntry((prev) => ({
            ...prev,
            loginEmailAddress: e.target.value.trim()
        }));
    };

    const onCommunicationEmailChange = (e) => {
        clearError('communicationEmailAddress');
        setNewEntry((prev) => ({
            ...prev,
            communicationEmailAddress: e.target.value.trim()
        }));
    };

    const onJobTitleChange = (e) => {
        setNewEntry((prev) => ({
            ...prev,
            jobTitle: e.target.value.trim().slice(0, MAX_JOB_TITLE_CHAR)
        }));
    };

    const onPhoneChange = (e) => {
        clearError('phoneNumber');
        setNewEntry((prev) => ({
            ...prev,
            phoneNumber: e.target.value.trim().slice(0, MAX_PHONE_CHAR)
        }));
    };

    const onContractsChange = (ids) => {
        if (!newEntry.id.length || compareUnorderedArrays(ids, newEntry.contractIds)) return;
        debouncedCallToLoadServices.current(ids);
    };

    const onPrimaryServiceChange = (chosenId) => {
        if (!newEntry.id.length) return;
        clearError('primaryServiceId');
        clearError('primaryTeamId');
        setNewEntry((prev) => ({ ...prev, primaryServiceId: chosenId }));
    };

    const onServicesChange = (ids) => {
        if (!newEntry.id.length || compareUnorderedArrays(ids, newEntry.otherServiceIds)) return;
        debouncedCallToUpdateServices.current(ids);
    };

    const onPrimaryTeamChange = (chosenId) => {
        if (!newEntry.id.length) return;
        clearError('primaryTeamId');
        setNewEntry((prev) => ({ ...prev, primaryTeamId: chosenId }));
    };

    const onTeamsChange = (ids) => {
        if (!newEntry.id.length || compareUnorderedArrays(ids, newEntry.otherTeamIds)) return;
        debouncedCallToUpdateTeams.current(ids);
    };

    const onUserTypesChange = (chosenIds) => {
        if (compareUnorderedArrays(chosenIds, newEntry.userTypeIds)) return;
        clearError('userTypeIds');
        if (!chosenIds) chosenIds = [];
        newEntry?.userTypeIds.length > 0 &&
            setNewEntry((prev) => ({ ...prev, userTypeIds: chosenIds }));
        setSelectedUserTypeIds(chosenIds);
    };

    const onMfaButtonClick = () => setShowMfaConfirmPrompt(true);

    const onConfirmMfaReset = (_, isConfirmed) => {
        if (isConfirmed) {
            newEntry.id && dispatch(mfaRequest(newEntry.id, _MFA_REQUEST_SENT_MESSAGE));
            setIsMfaButtonDisabled(true);
        }
        setShowMfaConfirmPrompt(false);
    };

    const onExit = () => {
        setKeys(clearKeys(keys));
        setNewEntry(INITIAL_STATE);
        onCancel();
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...newEntry,
            userTypeIds: selectedUserTypeIds
        };
        const newErrors = onValidate(payload, userTypes);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        if (newEntry.loginEmailAddress !== user.loginEmailAddress) {
            const isExisting = await checkEmailAddress(newEntry.loginEmailAddress);
            if (isExisting) {
                setErrors({
                    ...errors,
                    loginEmailAddress: { error: true, message: EMAIL_EXISTS }
                });
                return;
            }
        }
        dispatch(updateUser(payload));
    };

    let content = '';
    if (userTypes?.length < 1) content = 'No user types';
    if (userTypes?.length < 1) return <LoadingSpinner content={content} />;

    return (
        <div className={local.rowDropdownWrapper}>
            <form className={form.form} onSubmit={onSubmit} data-testid="form_start">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <div className={local.formToolbar}>
                            <div className={local.switchFormRow}>
                                <Switch
                                    id="locked"
                                    label="Locked"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    checked={newEntry.locked}
                                    onOff={true}
                                    inline={true}
                                    onChange={onLockedChange}
                                />
                                <Switch
                                    id="active"
                                    label="Active"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    checked={newEntry.active}
                                    onOff={true}
                                    inline={true}
                                    onChange={onActiveChange}
                                />
                            </div>
                            {!newEntry?.loginEmailAddress.endsWith('@reed.com') && (
                                <div className={local.mfaButton}>
                                    <Button
                                        id="mfaResetButton"
                                        type="button"
                                        disabled={
                                            !hasRole(acceptedRoles, roles) || isMfaButtonDisabled
                                        }
                                        content="Reset Multi Factor Authentication"
                                        onClick={onMfaButtonClick}
                                        error={errors.oathMfa}
                                        clearError={() => clearError('oathMfa')}
                                        customErrorClass="reduceTopMargin"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="payroll"
                            label="Payroll"
                            placeholder="Enter payroll number"
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.payroll || ''}
                            error={errors.payroll}
                            onChange={onPayrollChange}
                        />
                        <TextInputField
                            id="firstName"
                            label="First Name"
                            placeholder="Enter first name"
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.firstName || ''}
                            error={errors.firstName}
                            onChange={onFirstNameChange}
                        />
                        <TextInputField
                            id="lastName"
                            label="Last Name"
                            placeholder="Enter last name"
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.lastName || ''}
                            error={errors.lastName}
                            onChange={onLastNameChange}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <TextInputField
                            id="loginEmailAddress"
                            label="Login Email Address"
                            placeholder="Enter login email address"
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles)}
                            maxLength={MAX_EMAIL}
                            value={newEntry.loginEmailAddress || ''}
                            error={errors.loginEmailAddress}
                            onChange={onLoginEmailChange}
                        />
                        <TextInputField
                            id="communicationEmailAddress"
                            label="Communication Email Address"
                            placeholder="Enter communication email address"
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles)}
                            maxLength={MAX_EMAIL}
                            value={newEntry.communicationEmailAddress || ''}
                            error={errors.communicationEmailAddress}
                            onChange={onCommunicationEmailChange}
                        />

                        <TextInputField
                            id="jobTitle"
                            label="Job Title"
                            placeholder="Enter job title"
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.jobTitle || ''}
                            onChange={onJobTitleChange}
                        />

                        <TextInputField
                            id="phoneNumber"
                            name="phoneNumber"
                            label="Phone Number"
                            placeholder={'Enter phone number'}
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.phoneNumber || ''}
                            error={errors.phoneNumber}
                            onChange={onPhoneChange}
                        />
                    </div>
                </div>
                {newEntry.code && (
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <div className={form.staticContent}>
                                <label>User Code</label>
                                {newEntry.code}
                            </div>
                        </div>
                    </div>
                )}
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <div className={form.staticContent}>
                            <label>Selected Contracts</label>
                            <div className={pillClasses.staticPillList}>
                                {usersContracts.map((el) => (
                                    <span className={pillClasses.pill} key={el.id}>
                                        {el.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={form.formColumn}>
                        <div className="col2">
                            <MultiSelect
                                id="contracts"
                                key={keys.newContracts}
                                label="Contracts"
                                placeholder="Contracts"
                                disabled={!hasRole(acceptedRoles, roles)}
                                menuItems={contracts || []}
                                preSelectedIds={newEntry.contractIds || []}
                                preSelects={
                                    contracts.filter((el) =>
                                        newEntry.contractIds.includes(el.id)
                                    ) || []
                                }
                                onChange={(ids) => onContractsChange(ids)}
                            />
                        </div>
                    </div>
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <div className="">
                            <SingleSelect
                                id={'primaryServiceId'}
                                key={keys.primaryService}
                                label={'Primary Service'}
                                placeholder="Select a primary service..."
                                mandatory={true}
                                disabled={!hasRole(acceptedRoles, roles)}
                                menuItems={services || []}
                                selectedId={newEntry.primaryServiceId || ''}
                                selected={
                                    services.find((el) => el.id === newEntry.primaryServiceId) || {}
                                }
                                error={errors.primaryServiceId}
                                onChange={(chosenId) => onPrimaryServiceChange(chosenId)}
                            />
                        </div>

                        <div className={form.staticContent}>
                            <label>Other Services</label>
                            <div className={pillClasses.staticPillList}>
                                {usersOtherServices.map((el) => (
                                    <span className={pillClasses.pill} key={el.id}>
                                        {el.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={form.formColumn}>
                        <MultiSelect
                            id="services"
                            key={keys.newServices}
                            label="Services"
                            placeholder="Services"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={otherServices || []}
                            preSelectedIds={newEntry.otherServiceIds || []}
                            preSelects={
                                services.filter((el) => newEntry.otherServiceIds.includes(el.id)) ||
                                []
                            }
                            onChange={(ids) => onServicesChange(ids)}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'primaryTeamId'}
                            key={keys.primaryTeam}
                            label={'Primary Team'}
                            placeholder="Select a primary team..."
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={teamsForServices || []}
                            selectedId={newEntry.primaryTeamId || ''}
                            selected={
                                teamsForServices.find((el) => el.id === newEntry.primaryTeamId) ||
                                {}
                            }
                            error={errors.primaryTeamId}
                            onChange={(chosenId) => onPrimaryTeamChange(chosenId)}
                        />

                        <div className={form.staticContent}>
                            <label>Other Teams</label>
                            <div className={pillClasses.staticPillList}>
                                {usersOtherTeams?.map((el) => (
                                    <span className={pillClasses.pill} key={el.id}>
                                        {el.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="teams"
                            key={keys.newTeams}
                            label="Teams"
                            placeholder="Teams"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={otherTeams || []}
                            preSelectedIds={newEntry.otherTeamIds || []}
                            preSelects={
                                otherTeams.filter((el) => newEntry.otherTeamIds.includes(el.id)) ||
                                []
                            }
                            onChange={(ids) => onTeamsChange(ids)}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="userTypeIds"
                            key={keys.userTypeIds}
                            label="User Types"
                            placeholder="Add New User Types"
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles) || preventUserTypeChange}
                            selectAllDisabled={true}
                            menuItems={userTypes || []}
                            preSelectedIds={selectedUserTypeIds}
                            preSelects={
                                userTypes.filter((el) => selectedUserTypeIds.includes(el.id)) || []
                            }
                            error={errors.userTypeIds}
                            onChange={(chosenIds) => onUserTypesChange(chosenIds)}
                        />
                    </div>

                    <div className={form.formColumn}></div>
                </div>

                <div className={form.formActions}>
                    <Button
                        id="editUserDetailsButton"
                        content="Update User Details"
                        disabled={!hasRole(acceptedRoles, roles)}
                    />
                    <div className={form.cancelLink} onClick={onExit}>
                        Cancel X
                    </div>
                </div>
            </form>
            {showMfaConfirmPrompt && (
                <ConfirmPrompt
                    isOpen={showMfaConfirmPrompt}
                    onCancel={(e) => onConfirmMfaReset(e, false)}
                    onConfirm={(e) => onConfirmMfaReset(e, true)}
                    content={_MFA_CONFIRM_MESSAGE}
                />
            )}
        </div>
    );
};

export default EditUsersDetails;

EditUsersDetails.propTypes = {
    row: PropTypes.object,
    onClose: PropTypes.func,
    onCancel: PropTypes.func
};
